import React from 'react'
import { cn } from '@cannect/lib/utils'
import useIsPrescriber from '@cannect/hooks/useIsPrescriber'
import {
  CURATORSHIP_TICKS,
  CURATORSHIP_TIERS,
  MIN_VISIBLE_PERCENTAGE,
  clampPercentage,
  formatPercentage,
  getCuratorshipTier
} from './tiers'

const FULL_SIZES = {
  sm: {
    maxWidth: 'max-w-xs',
    topSpace: 'pt-8',
    markerBox: 'px-2 py-0.5 text-xs',
    markerArrow: 'border-x-4 border-t-[5px]',
    track: 'h-3',
    pin: 'top-1.5 h-[22px] w-0.5',
    ticksRow: 'mt-1 h-3.5',
    tickText: 'text-[9px]',
    ticks: [0, 50, 100]
  },
  md: {
    maxWidth: 'max-w-sm',
    topSpace: 'pt-9',
    markerBox: 'px-2.5 py-1 text-xs',
    markerArrow: 'border-x-[5px] border-t-[6px]',
    track: 'h-4',
    pin: 'top-2 h-[26px] w-[2px]',
    ticksRow: 'mt-1 h-4',
    tickText: 'text-[10px]',
    ticks: [0, 30, 50, 70, 100]
  },
  lg: {
    maxWidth: 'max-w-lg',
    topSpace: 'pt-11',
    markerBox: 'px-3 py-1.5 text-sm',
    markerArrow: 'border-x-[6px] border-t-[7px]',
    track: 'h-[22px]',
    pin: 'top-3.5 h-[34px] w-[3px]',
    ticksRow: 'mt-1.5 h-4',
    tickText: 'text-[11px]',
    ticks: CURATORSHIP_TICKS
  }
} as const

const DEFAULT_CAPTION = 'Aderência aos critérios da Curadoria Técnica de Produtos'

interface CuratorshipGaugeProps {
  percentage?: number | null
  variant?: 'compact' | 'full'
  size?: keyof typeof FULL_SIZES
  publicContent?: boolean
  hideWhenEmpty?: boolean
  caption?: string | false
  className?: string
}


const CuratorshipGauge: React.FC<CuratorshipGaugeProps> = ({
  percentage,
  variant = 'compact',
  size = 'md',
  publicContent = false,
  hideWhenEmpty = true,
  caption = DEFAULT_CAPTION,
  className
}) => {
  const isPrescriber = useIsPrescriber()
  const value = clampPercentage(percentage)

  if (!publicContent && !isPrescriber) return null

  if (hideWhenEmpty && (value === null || value < MIN_VISIBLE_PERCENTAGE)) return null

  const safeValue = value ?? 0
  const tier = getCuratorshipTier(safeValue)
  const label = formatPercentage(safeValue)
  const description = `Aderência de ${label} aos critérios da Curadoria`

  if (variant === 'compact') {
    return (
      <div className={cn('inline-flex items-center gap-2', className)} aria-label={description} title={description}>
        <span className="h-1.5 w-16 overflow-hidden rounded-full bg-neutral-200">
          <span className={cn('block h-full rounded-full', tier.className)} style={{ width: `${safeValue}%` }} />
        </span>
        <span className="text-xs font-semibold leading-none text-primary-900">{label}</span>
      </div>
    )
  }

  const scale = FULL_SIZES[size]

  return (
    <div className={cn('w-full', scale.maxWidth, className)} aria-label={description}>
      <div className={cn('relative', scale.topSpace)}>
        <div
          className="absolute top-0 z-10 flex -translate-x-1/2 flex-col items-center transition-[left] duration-200"
          style={{ left: `${safeValue}%` }}>
          <span
            className={cn(
              'whitespace-nowrap rounded-lg bg-primary-900 font-bold text-white shadow-md',
              scale.markerBox
            )}>
            {label}
          </span>
          <span className={cn('-mt-px size-0 border-x-transparent border-t-primary-900', scale.markerArrow)} />
        </div>

        <div
          className={cn(
            'relative flex w-full overflow-visible rounded-full shadow-[inset_0_1px_3px_rgba(0,0,0,0.15)]',
            scale.track
          )}>
          {CURATORSHIP_TIERS.map((item, index) => (
            <span
              key={item.key}
              className={cn(
                'h-full',
                item.className,
                index === 0 && 'rounded-l-full',
                index === CURATORSHIP_TIERS.length - 1 && 'rounded-r-full'
              )}
              style={{ width: `${item.width}%` }}
            />
          ))}

          <span
            className={cn('absolute -translate-x-1/2 rounded-sm bg-primary-900', scale.pin)}
            style={{ left: `${safeValue}%` }}
          />
        </div>

        <div className={cn('relative', scale.ticksRow)}>
          {scale.ticks.map((tick, index) => (
            <span
              key={tick}
              className={cn(
                'absolute whitespace-nowrap text-neutral-500',
                scale.tickText,
                index === 0 && 'translate-x-0',
                index > 0 && index < scale.ticks.length - 1 && '-translate-x-1/2',
                index === scale.ticks.length - 1 && '-translate-x-full'
              )}
              style={{ left: `${tick}%` }}>
              {tick === 0 ? '0' : `${tick}%`}
            </span>
          ))}
        </div>
      </div>

      {caption && <p className="mt-1 text-xs leading-snug text-neutral-500">{caption}</p>}
    </div>
  )
}

export default CuratorshipGauge
